/*
  <slider-carousel> — progressive enhancement for a CSS scroll-snap track
  (styles: .slider* in component-base.css).

  The track already scrolls and snaps without this script (touch, trackpad, keyboard).
  The script wires the previous/next buttons, marks them aria-disabled at either end
  (aria-disabled rather than disabled, so a focused button keeps focus), and sets
  [data-overflow] on the element while the track holds more than fits — the CSS hides
  the buttons otherwise.

  Markup:
    <slider-carousel class="slider">
      {% render 'slider-buttons', track_id: 'Slider-ID' %}   (anywhere inside the element)
      <ul class="slider__track" id="Slider-ID" role="list" data-slider-track>
        <li class="slider__slide">…</li>
      </ul>
    </slider-carousel>

  Buttons that belong to a nested <slider-carousel> are ignored. Load the script as a
  module from each section that renders a slider; modules run once per page however
  many sections include them.

  Theme editor: selecting a block inside the track scrolls it into view.
*/
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class SliderCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-slider-track]');
    if (!this.track) return;

    this.prevButtons = this.ownElements('[data-slider-prev]');
    this.nextButtons = this.ownElements('[data-slider-next]');

    this.handlePrev = () => this.go(-1);
    this.handleNext = () => this.go(1);
    this.handleChange = () => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.update();
      });
    };
    this.handleBlockSelect = (event) => this.reveal(event.target);

    this.prevButtons.forEach((button) => button.addEventListener('click', this.handlePrev));
    this.nextButtons.forEach((button) => button.addEventListener('click', this.handleNext));
    this.track.addEventListener('scroll', this.handleChange, { passive: true });
    document.addEventListener('shopify:block:select', this.handleBlockSelect);

    // Also fires when a hidden tab panel holding the slider is opened.
    this.resizeObserver = new ResizeObserver(this.handleChange);
    this.resizeObserver.observe(this.track);

    this.update();
  }

  disconnectedCallback() {
    if (!this.track) return;
    this.prevButtons.forEach((button) => button.removeEventListener('click', this.handlePrev));
    this.nextButtons.forEach((button) => button.removeEventListener('click', this.handleNext));
    this.track.removeEventListener('scroll', this.handleChange);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    this.resizeObserver.disconnect();
    cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  ownElements(selector) {
    return Array.from(this.querySelectorAll(selector)).filter((element) => element.closest('slider-carousel') === this);
  }

  // Moves by the number of whole slides in view, so a click never skips a slide.
  go(direction) {
    const buttons = direction < 0 ? this.prevButtons : this.nextButtons;
    if (buttons.length && buttons[0].getAttribute('aria-disabled') === 'true') return;

    const slide = this.track.firstElementChild;
    if (!slide) return;

    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const step = slide.getBoundingClientRect().width + gap;
    const perPage = Math.max(1, Math.floor((this.track.clientWidth + gap) / step));

    this.track.scrollBy({
      left: direction * perPage * step,
      behavior: reducedMotion.matches ? 'auto' : 'smooth',
    });
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const maxScroll = scrollWidth - clientWidth;
    const overflow = maxScroll > 1;

    this.toggleAttribute('data-overflow', overflow);
    this.prevButtons.forEach((button) => button.setAttribute('aria-disabled', String(!overflow || scrollLeft <= 1)));
    this.nextButtons.forEach((button) => button.setAttribute('aria-disabled', String(!overflow || scrollLeft >= maxScroll - 1)));
  }

  reveal(target) {
    if (!(target instanceof Element) || !this.track.contains(target)) return;

    const slide = Array.from(this.track.children).find((child) => child.contains(target));
    if (!slide) return;

    const padding = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
    this.track.scrollTo({ left: slide.offsetLeft - padding, behavior: 'auto' });
  }
}

if (!customElements.get('slider-carousel')) {
  customElements.define('slider-carousel', SliderCarousel);
}
